import ActionTypes from '../actions/types'
import { createReducer } from 'reduxsauce'
import { Constants } from 'galarm-config'
import uuid from 'react-native-uuid'
import Utils from '../utils/Utils'
import arrayDifferenceWith from 'lodash/differenceWith'

const INITIAL_STATE = {
  pendingActions: [],
  softPendingActions: []
}

const addAddAlarmPendingAction = (state, action) => {
  const pendingAction = {
    name: Constants.ADD_ALARM_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm
  }
  return {
    ...state,
    pendingActions: [...state.pendingActions, pendingAction]
  }
}

const addAddChecklistPendingAction = (state, action) => {
  const pendingAction = {
    name: Constants.ADD_CHECKLIST_PENDING_ACTION_NAME,
    id: uuid.v4(),
    checklist: action.payload.checklist
  }
  return {
    ...state,
    pendingActions: [...state.pendingActions, pendingAction]
  }
}

const addEnableDisableAlarmPendingAction = (state, action) => {
  // eslint-disable-line no-unused-vars
  const existingEnableDisableAlarmPendingActionIndex =
    state.pendingActions.findIndex(pendingAction => {
      return (
        pendingAction.name ===
          Constants.ADD_ENABLE_DISABLE_ALARM_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.alarm.id
      )
    })

  let newPendingActions = state.pendingActions
  if (existingEnableDisableAlarmPendingActionIndex !== -1) {
    newPendingActions = state.pendingActions.filter(
      (_, index) => index !== existingEnableDisableAlarmPendingActionIndex
    )
  }

  const pendingAction = {
    name: Constants.ADD_ENABLE_DISABLE_ALARM_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm,
    timestamp: action.payload.timestamp,
    alarmStatus: action.payload.alarmStatus
  }

  return {
    ...state,
    pendingActions: [...newPendingActions, pendingAction]
  }
}

const removePendingAction = (state, action) => {
  console.log('Remove pending action ' + action.payload.id)
  const actionIndex = Utils.getIndexOfObjectWithId(
    state.pendingActions,
    action.payload.id
  )
  if (actionIndex !== -1) {
    const newPendingActions = state.pendingActions.filter(
      (_, index) => index !== actionIndex
    )
    return {
      ...state,
      pendingActions: newPendingActions
    }
  } else {
    console.log('Trying to remove a non-existent pending action ', action)
    return state
  }
}

const updatePendingActionRetryCount = (state, action) => {
  const actionIndex = Utils.getIndexOfObjectWithId(
    state.pendingActions,
    action.payload.id
  )
  if (actionIndex === -1) return state
  const updated = {
    ...state.pendingActions[actionIndex],
    retryCount: (state.pendingActions[actionIndex].retryCount || 0) + 1
  }
  const newPendingActions = [...state.pendingActions]
  newPendingActions[actionIndex] = updated
  return { ...state, pendingActions: newPendingActions }
}

const removeSoftPendingAction = (state, action) => {
  console.log('Remove soft pending action ' + action.payload.id)
  const actionIndex = Utils.getIndexOfObjectWithId(
    state.softPendingActions,
    action.payload.id
  )
  if (actionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== actionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    console.log('Trying to remove a non-existent pending action ', action)
    return state
  }
}

const updateSoftPendingActionRetryCount = (state, action) => {
  const actionIndex = Utils.getIndexOfObjectWithId(
    state.softPendingActions,
    action.payload.id
  )
  if (actionIndex === -1) return state
  const updated = {
    ...state.softPendingActions[actionIndex],
    retryCount: (state.softPendingActions[actionIndex].retryCount || 0) + 1
  }
  const newSoftPendingActions = [...state.softPendingActions]
  newSoftPendingActions[actionIndex] = updated
  return { ...state, softPendingActions: newSoftPendingActions }
}

const removeAllPendingActions = state => {
  return {
    ...state,
    pendingActions: [],
    softPendingActions: []
  }
}

const addDeleteAlarmPendingAction = (state, action) => {
  const getAlarmIdForPendingAction = pendingAction => {
    let pendingActionAlarmId = ''
    if (pendingAction.alarmId) {
      pendingActionAlarmId = pendingAction.alarmId
    } else if (pendingAction.alarm) {
      pendingActionAlarmId = pendingAction.alarm.id
    } else if (pendingAction.editedAlarm) {
      pendingActionAlarmId = pendingAction.editedAlarm.id
    }
    return pendingActionAlarmId
  }

  // If there are any existing pending actions for this alarm, remove them
  const existingPendingActionsForAlarm = state.pendingActions.filter(
    pendingAction => {
      const pendingActionAlarmId = getAlarmIdForPendingAction(pendingAction)
      return pendingActionAlarmId === action.payload.alarm.id
    }
  )

  const existingSoftPendingActionsForAlarm = state.softPendingActions.filter(
    pendingAction => {
      const pendingActionAlarmId = getAlarmIdForPendingAction(pendingAction)
      return pendingActionAlarmId === action.payload.alarm.id
    }
  )

  let newPendingActions = state.pendingActions
  let newSoftPendingActions = state.softPendingActions

  if (existingPendingActionsForAlarm.length > 0) {
    newPendingActions = arrayDifferenceWith(
      state.pendingActions,
      existingPendingActionsForAlarm,
      (action1, action2) => action1.id === action2.id
    )
  }

  if (existingSoftPendingActionsForAlarm.length > 0) {
    newSoftPendingActions = arrayDifferenceWith(
      state.softPendingActions,
      existingSoftPendingActionsForAlarm,
      (action1, action2) => action1.id === action2.id
    )
  }

  const pendingAction = {
    name: Constants.DELETE_ALARM_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm
  }

  return {
    ...state,
    pendingActions: [...newPendingActions, pendingAction],
    softPendingActions: newSoftPendingActions
  }
}

const addRemoveChecklistPendingAction = (state, action) => {
  const { checklistId } = action.payload

  const newChecklistPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.ADD_CHECKLIST_PENDING_ACTION_NAME &&
        pendingAction.checklist.id === checklistId
      )
    }
  )

  if (newChecklistPendingActionIndex !== -1) {
    const newPendingActions = state.pendingActions.filter(
      (_, index) => index !== newChecklistPendingActionIndex
    )
    return {
      ...state,
      pendingActions: newPendingActions
    }
  }

  const editChecklistPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.EDIT_CHECKLIST_PENDING_ACTION_NAME &&
        pendingAction.checklist.id === checklistId
      )
    }
  )

  const pendingAction = {
    name: Constants.REMOVE_CHECKLIST_PENDING_ACTION_NAME,
    id: uuid.v4(),
    checklistId
  }

  let newPendingActions
  if (editChecklistPendingActionIndex !== -1) {
    newPendingActions = state.pendingActions.map((action, index) =>
      index === editChecklistPendingActionIndex ? pendingAction : action
    )
  } else {
    newPendingActions = [...state.pendingActions, pendingAction]
  }

  return {
    ...state,
    pendingActions: newPendingActions
  }
}

const addRemoveRemoteAlarmNotificationPendingAction = (state, action) => {
  const pendingAction = {
    name: Constants.REMOVE_REMOTE_ALARM_NOTIFICATION_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmId: action.payload.alarmId
  }
  const newPendingActions = [...state.pendingActions, pendingAction]
  return {
    ...state,
    pendingActions: newPendingActions
  }
}

const addEditAlarmPendingAction = (state, action) => {
  const { editedAlarm, prevAlarm, removeSubscription, wipeAlarmActions } =
    action.payload

  // If there is a pending action to create new alarm with same id, then the alarm has not been
  // pushed to the cloud yet. Just update the add operation to add the edited alarm
  const newAlarmPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.ADD_ALARM_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.editedAlarm.id
      )
    }
  )

  if (newAlarmPendingActionIndex !== -1) {
    const pendingAction = {
      name: Constants.ADD_ALARM_PENDING_ACTION_NAME,
      id: uuid.v4(),
      alarm: editedAlarm
    }
    const newPendingActions = [
      ...state.pendingActions.filter(
        (_, index) => index !== newAlarmPendingActionIndex
      ),
      pendingAction
    ]
    return {
      ...state,
      pendingActions: newPendingActions
    }
  }

  // Check if we already have an edit alarm pending action for the same alarm
  // If yes, then merge the two
  const editAlarmPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.EDIT_ALARM_PENDING_ACTION_NAME &&
        pendingAction.editedAlarm.id === action.payload.editedAlarm.id
      )
    }
  )

  let pendingAction = null
  let newPendingActions

  if (editAlarmPendingActionIndex !== -1) {
    const prevPendingAction = state.pendingActions[editAlarmPendingActionIndex]
    pendingAction = {
      name: Constants.EDIT_ALARM_PENDING_ACTION_NAME,
      id: uuid.v4(),
      editedAlarm,
      prevAlarm: prevPendingAction.prevAlarm,
      removeSubscription,
      wipeAlarmActions: prevPendingAction.wipeAlarmActions || wipeAlarmActions
    }
    newPendingActions = [
      ...state.pendingActions.filter(
        (_, index) => index !== editAlarmPendingActionIndex
      ),
      pendingAction
    ]
  } else {
    pendingAction = {
      name: Constants.EDIT_ALARM_PENDING_ACTION_NAME,
      id: uuid.v4(),
      editedAlarm,
      prevAlarm,
      removeSubscription,
      wipeAlarmActions
    }
    newPendingActions = [...state.pendingActions, pendingAction]
  }

  return {
    ...state,
    pendingActions: newPendingActions
  }
}

const addEditChecklistPendingAction = (state, action) => {
  const { checklist } = action.payload

  // If there is a pending action to create new alarm with same id, then the alarm has not been
  // pushed to the cloud yet. Just update the add operation to add the edited alarm
  const newChecklistPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.ADD_CHECKLIST_PENDING_ACTION_NAME &&
        pendingAction.checklist.id === checklist.id
      )
    }
  )

  if (newChecklistPendingActionIndex !== -1) {
    const pendingAction = {
      name: Constants.ADD_CHECKLIST_PENDING_ACTION_NAME,
      id: uuid.v4(),
      checklist: checklist
    }
    const newPendingActions = state.pendingActions.map((action, index) =>
      index === newChecklistPendingActionIndex ? pendingAction : action
    )
    return {
      ...state,
      pendingActions: newPendingActions
    }
  }

  // Check if we already have an edit alarm pending action for the same alarm
  // If yes, then merge the two
  const editChecklistPendingActionIndex = state.pendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name === Constants.EDIT_CHECKLIST_PENDING_ACTION_NAME &&
        pendingAction.checklist.id === checklist.id
      )
    }
  )

  const pendingAction = {
    name: Constants.EDIT_CHECKLIST_PENDING_ACTION_NAME,
    id: uuid.v4(),
    checklist
  }

  let newPendingActions
  if (editChecklistPendingActionIndex !== -1) {
    newPendingActions = state.pendingActions.map((action, index) =>
      index === editChecklistPendingActionIndex ? pendingAction : action
    )
  } else {
    newPendingActions = [...state.pendingActions, pendingAction]
  }

  return {
    ...state,
    pendingActions: newPendingActions
  }
}

const addStartSnoozeForCreatorSoftPendingAction = (state, action) => {
  const { alarmId, snoozeInterval, snoozeDate } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.STOP_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarmId === alarmId
      )
    }
  )

  let newSoftPendingActions = state.softPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
  }

  const softPendingAction = {
    name: Constants.START_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmId,
    snoozeInterval,
    snoozeDate
  }

  return {
    ...state,
    softPendingActions: [...newSoftPendingActions, softPendingAction]
  }
}

const addStopSnoozeForCreatorSoftPendingAction = (state, action) => {
  const { alarmId, scheduledRemotely } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.START_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarmId === alarmId
      )
    }
  )

  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  }

  const softPendingAction = {
    name: Constants.STOP_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmId,
    scheduledRemotely: scheduledRemotely || false
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addStartSnoozeForParticipantSoftPendingAction = (state, action) => {
  const { alarm, snoozeInterval, snoozeDate } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.STOP_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === alarm.id
      )
    }
  )

  let newSoftPendingActions = state.softPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
  }

  const softPendingAction = {
    name: Constants.START_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    snoozeInterval,
    snoozeDate
  }

  return {
    ...state,
    softPendingActions: [...newSoftPendingActions, softPendingAction]
  }
}

const addStopSnoozeForParticipantSoftPendingAction = (state, action) => {
  const { alarm } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.START_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === alarm.id
      )
    }
  )

  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  }

  const softPendingAction = {
    name: Constants.STOP_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addMarkPersonalAlarmDoneSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.MARK_PERSONAL_ALARM_DONE_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addSkipPersonalAlarmSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.SKIP_PERSONAL_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addSkipPersonalParticipantAlarmSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.SKIP_PERSONAL_PARTICIPANT_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addSkipRecipientAlarmSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.SKIP_RECIPIENT_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addSkipRecipientCreatorAlarmSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.SKIP_RECIPIENT_CREATOR_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addUpdateRecipientAlarmDateAndTimeSoftPendingAction = (state, action) => {
  const { alarm, newRecipientDate, originalRecipientDate, currDate } =
    action.payload
  const softPendingAction = {
    name: Constants.UPDATE_RECIPIENT_ALARM_DATE_TIME_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    newRecipientDate,
    originalRecipientDate,
    currDate
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addMarkRecipientAlarmDoneSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp } = action.payload
  const softPendingAction = {
    name: Constants.MARK_RECIPIENT_ALARM_DONE_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addMarkCreatorResponseForGroupAlarmSoftPendingAction = (
  state,
  action
) => {
  const { alarm, response, rescheduleAlarm, occurrenceTime, timestamp } =
    action.payload

  const softPendingAction = {
    name: Constants.MARK_CREATOR_RESPONSE_FOR_GROUP_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    response,
    rescheduleAlarm,
    occurrenceTime,
    timestamp
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addMarkParticipantResponseForGroupAlarmSoftPendingAction = (
  state,
  action
) => {
  const {
    alarm,
    response,
    rescheduleAlarm,
    updateAlarmResponseStatus,
    occurrenceTime,
    timestamp
  } = action.payload

  const softPendingAction = {
    name: Constants.MARK_PARTICIPANT_RESPONSE_FOR_GROUP_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    response,
    rescheduleAlarm,
    updateAlarmResponseStatus,
    occurrenceTime,
    timestamp
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addSetParticipantAlarmResponseSoftPendingAction = (state, action) => {
  const { alarm, participantResponse } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.SET_PARTICIPANT_ALARM_RESPONSE_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === alarm.id
      )
    }
  )

  // Update the same action if it already exists such that the previous
  // response status is picked correctly when the action is persisted
  if (pendingActionIndex !== -1) {
    const softPendingAction = state.softPendingActions[pendingActionIndex]
    const newSoftPendingActions = state.softPendingActions.map(
      (action, index) =>
        index === pendingActionIndex
          ? { ...softPendingAction, participantResponse }
          : action
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const softPendingAction = {
      name: Constants.SET_PARTICIPANT_ALARM_RESPONSE_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      alarm,
      participantResponse
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, softPendingAction]
    }
  }
}

const addSetRecipientAlarmResponseSoftPendingAction = (state, action) => {
  const { alarm, participantResponse } = action.payload
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.SET_RECIPIENT_ALARM_RESPONSE_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === alarm.id
      )
    }
  )

  // Update the same action if it already exists such that the previous
  // response status is picked correctly when the action is persisted
  if (pendingActionIndex !== -1) {
    const softPendingAction = state.softPendingActions[pendingActionIndex]
    const newSoftPendingActions = state.softPendingActions.map(
      (action, index) =>
        index === pendingActionIndex
          ? { ...softPendingAction, participantResponse }
          : action
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const softPendingAction = {
      name: Constants.SET_RECIPIENT_ALARM_RESPONSE_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      alarm,
      participantResponse
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, softPendingAction]
    }
  }
}

const addSetPastAlarmOccurrenceResponseSoftPendingAction = (state, action) => {
  const { alarm, occurrenceTime, timestamp, response } = action.payload
  const softPendingAction = {
    name: Constants.SET_PAST_ALARM_OCCURRENCE_RESPONSE_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm,
    occurrenceTime,
    timestamp,
    response
  }
  return {
    ...state,
    softPendingActions: [...state.softPendingActions, softPendingAction]
  }
}

const addUpdateParticipantAlarmRingerSettingsSoftPendingAction = (
  state,
  action
) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.ADD_UPDATE_PARTICIPANT_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.alarm.id
      )
    }
  )

  const pendingAction = {
    name: Constants.ADD_UPDATE_PARTICIPANT_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm,
    ringerSettings: action.payload.ringerSettings
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateGestureToDismissAnAlarmSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_GESTURE_TO_DISMISS_AN_ALARM_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_GESTURE_TO_DISMISS_AN_ALARM_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    gestureToDismissAnAlarm: action.payload.gestureToDismissAnAlarm
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdatePreReminderRingtoneSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_PRE_REMINDER_RINGTONE_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_PRE_REMINDER_RINGTONE_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    preReminderRingtone: action.payload.preReminderRingtone
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateTimerRingtoneSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_TIMER_RINGTONE_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_TIMER_RINGTONE_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    timerRingtone: action.payload.timerRingtone
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateMuteAlarmsSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_MUTE_ALARMS_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_MUTE_ALARMS_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    muteAlarms: action.payload.muteAlarms
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateNotificationSettingsSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_NOTIFICATION_SETTINGS_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_NOTIFICATION_SETTINGS_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    notificationSettings: action.payload.notificationSettings
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateGraduallyIncreaseVolumeSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_GRADUALLY_INCREASE_VOLUME_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_GRADUALLY_INCREASE_VOLUME_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      graduallyIncreaseVolume: action.payload.graduallyIncreaseVolume
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateShakeToStopAlarmSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_SHAKE_TO_STOP_ALARM_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_SHAKE_TO_STOP_ALARM_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      shakeToStopAlarm: action.payload.shakeToStopAlarm
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateShakeSensitivitySoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_SHAKE_SENSITIVITY_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_SHAKE_SENSITIVITY_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      shakeSensitivity: action.payload.shakeSensitivity
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdatePinModeSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_PIN_MODE_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_PIN_MODE_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      pinMode: action.payload.pinMode
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateNotificationActionBehaviorSoftPendingAction = (
  state,
  action
) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_NOTIFICATION_ACTION_BEHAVIOR_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_NOTIFICATION_ACTION_BEHAVIOR_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      notificationActionBehavior: action.payload.notificationActionBehavior
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdatePromptForUpdateSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_PROMPT_FOR_UPDATE_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_PROMPT_FOR_UPDATE_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      promptForUpdate: action.payload.promptForUpdate
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateSortAlarmCategoriesBySoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_SORT_ALARM_CATEGORIES_BY_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_SORT_ALARM_CATEGORIES_BY_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      sortAlarmCategoriesBy: action.payload.sortAlarmCategoriesBy
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateMissedAlarmPlacementSettingSoftPendingAction = (
  state,
  action
) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_MISSED_ALARMS_PLACEMENT_SETTINGS_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = [...state.softPendingActions]
    newSoftPendingActions[pendingActionIndex] = {
      ...newSoftPendingActions[pendingActionIndex],
      missedAlarmPlacementSetting: action.payload.missedAlarmPlacementSetting
    }
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_MISSED_ALARMS_PLACEMENT_SETTINGS_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      missedAlarmPlacementSetting: action.payload.missedAlarmPlacementSetting
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateFabButtonBehaviorSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction =>
      pendingAction.name ===
      Constants.UPDATE_FAB_BUTTON_BEHAVIOR_SOFT_PENDING_ACTION_NAME
  )
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = [...state.softPendingActions]
    newSoftPendingActions[pendingActionIndex] = {
      ...newSoftPendingActions[pendingActionIndex],
      fabButtonBehavior: action.payload.fabButtonBehavior
    }
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.UPDATE_FAB_BUTTON_BEHAVIOR_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      fabButtonBehavior: action.payload.fabButtonBehavior
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateDefaultTimezoneForAlarmSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.ADD_UPDATE_DEFAULT_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.ADD_UPDATE_DEFAULT_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      defaultTimezoneForAlarm: action.payload.defaultTimezoneForAlarm
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateSpecifyTimezoneForAlarmSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.ADD_UPDATE_SPECIFY_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION_NAME
      )
    }
  )
  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.ADD_UPDATE_SPECIFY_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      specifyTimezoneForAlarm: action.payload.specifyTimezoneForAlarm
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addUpdateRingOnEarphoneOnlySoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.UPDATE_RING_ON_EARPHONE_ONLY_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.UPDATE_RING_ON_EARPHONE_ONLY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    ringOnEarphoneOnly: action.payload.ringOnEarphoneOnly
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateParticipantAlarmPreReminderDurationSoftPendingAction = (
  state,
  action
) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.ADD_UPDATE_PARTICIPANT_ALARM_PRE_REMINDER_DURATION_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.alarm.id
      )
    }
  )

  const pendingAction = {
    name: Constants.ADD_UPDATE_PARTICIPANT_ALARM_PRE_REMINDER_DURATION_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm,
    preReminderDuration: action.payload.preReminderDuration
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateAlarmPreReminderDurationSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.ADD_UPDATE_ALARM_PRE_REMINDER_DURATION_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.alarm.id
      )
    }
  )

  const pendingAction = {
    name: Constants.ADD_UPDATE_ALARM_PRE_REMINDER_DURATION_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm,
    preReminderDuration: action.payload.preReminderDuration
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateAlarmRingerSettingsSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
          Constants.ADD_UPDATE_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarm.id === action.payload.alarm.id
      )
    }
  )

  const pendingAction = {
    name: Constants.ADD_UPDATE_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarm: action.payload.alarm,
    ringerSettings: action.payload.ringerSettings
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateGlobalAlarmRingerSettingsSoftPendingAction = (state, action) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.ADD_UPDATE_GLOBAL_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  const pendingAction = {
    name: Constants.ADD_UPDATE_GLOBAL_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    ringerSettings: action.payload.ringerSettings
  }

  let newSoftPendingActions
  if (pendingActionIndex !== -1) {
    newSoftPendingActions = [
      ...state.softPendingActions.filter(
        (_, index) => index !== pendingActionIndex
      ),
      pendingAction
    ]
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addUpdateRingParticipantAlarmsByDefaultSettingSoftPendingAction = (
  state,
  action
) => {
  const pendingActionIndex = state.softPendingActions.findIndex(
    pendingAction => {
      return (
        pendingAction.name ===
        Constants.ADD_UPDATE_RING_PARTICIPANT_ALARMS_BY_DEFAULT_SETTING_SOFT_PENDING_ACTION_NAME
      )
    }
  )

  // If there is already a pending action to update the setting, then just remove the pending action
  // as if it were previously disabled, now it must be enabled which should be the current value on
  // the database and vice versa
  if (pendingActionIndex !== -1) {
    const newSoftPendingActions = state.softPendingActions.filter(
      (_, index) => index !== pendingActionIndex
    )
    return {
      ...state,
      softPendingActions: newSoftPendingActions
    }
  } else {
    const pendingAction = {
      name: Constants.ADD_UPDATE_RING_PARTICIPANT_ALARMS_BY_DEFAULT_SETTING_SOFT_PENDING_ACTION_NAME,
      id: uuid.v4(),
      ringParticipantAlarmsByDefault:
        action.payload.ringParticipantAlarmsByDefault
    }
    return {
      ...state,
      softPendingActions: [...state.softPendingActions, pendingAction]
    }
  }
}

const addAddAlarmToAlarmCategorySoftPendingAction = (state, action) => {
  const { alarmCategoryId, alarmId } = action.payload

  const pendingAction = {
    name: Constants.ADD_ALARM_TO_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmCategoryId,
    alarmId
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, pendingAction]
  }
}

const addRemoveAlarmFromAlarmCategorySoftPendingAction = (state, action) => {
  const { alarmCategoryId, alarmId } = action.payload

  const pendingAction = {
    name: Constants.REMOVE_ALARM_FROM_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmCategoryId,
    alarmId
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, pendingAction]
  }
}

const addDeleteAlarmCategorySoftPendingAction = (state, action) => {
  const { alarmCategoryId } = action.payload

  const newSoftPendingActions = state.softPendingActions.filter(
    pendingAction => pendingAction.alarmCategoryId !== alarmCategoryId
  )

  const pendingAction = {
    name: Constants.DELETE_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmCategoryId
  }

  return {
    ...state,
    softPendingActions: [...newSoftPendingActions, pendingAction]
  }
}

const addUpdateAlarmCategorySoftPendingAction = (state, action) => {
  const { alarmCategoryId, alarmCategory } = action.payload

  const pendingAction = {
    name: Constants.UPDATE_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmCategoryId,
    alarmCategory
  }

  const existingPendingActionForCategoryIndex =
    state.softPendingActions.findIndex(
      pendingAction =>
        pendingAction.name ===
          Constants.UPDATE_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME &&
        pendingAction.alarmCategoryId === alarmCategoryId
    )

  let newSoftPendingActions
  if (existingPendingActionForCategoryIndex !== -1) {
    newSoftPendingActions = state.softPendingActions.map((action, index) =>
      index === existingPendingActionForCategoryIndex ? pendingAction : action
    )
  } else {
    newSoftPendingActions = [...state.softPendingActions, pendingAction]
  }

  return {
    ...state,
    softPendingActions: newSoftPendingActions
  }
}

const addCreateAlarmCategorySoftPendingAction = (state, action) => {
  const { alarmCategoryId, alarmCategory } = action.payload

  const pendingAction = {
    name: Constants.CREATE_ALARM_CATEGORY_SOFT_PENDING_ACTION_NAME,
    id: uuid.v4(),
    alarmCategoryId,
    alarmCategory
  }

  return {
    ...state,
    softPendingActions: [...state.softPendingActions, pendingAction]
  }
}

const ACTION_HANDLERS = {
  [ActionTypes.ADD_ADD_ALARM_PENDING_ACTION]: addAddAlarmPendingAction,
  [ActionTypes.REMOVE_PENDING_ACTION]: removePendingAction,
  [ActionTypes.REMOVE_ALL_PENDING_ACTIONS]: removeAllPendingActions,
  [ActionTypes.UPDATE_PENDING_ACTION_RETRY_COUNT]:
    updatePendingActionRetryCount,
  [ActionTypes.UPDATE_SOFT_PENDING_ACTION_RETRY_COUNT]:
    updateSoftPendingActionRetryCount,
  [ActionTypes.ADD_DELETE_ALARM_PENDING_ACTION]: addDeleteAlarmPendingAction,
  [ActionTypes.ADD_EDIT_ALARM_PENDING_ACTION]: addEditAlarmPendingAction,
  [ActionTypes.ADD_ENABLE_DISABLE_ALARM_PENDING_ACTION]:
    addEnableDisableAlarmPendingAction,
  [ActionTypes.ADD_START_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION]:
    addStartSnoozeForCreatorSoftPendingAction,
  [ActionTypes.ADD_STOP_SNOOZE_FOR_CREATOR_SOFT_PENDING_ACTION]:
    addStopSnoozeForCreatorSoftPendingAction,
  [ActionTypes.ADD_START_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION]:
    addStartSnoozeForParticipantSoftPendingAction,
  [ActionTypes.ADD_STOP_SNOOZE_FOR_PARTICIPANT_SOFT_PENDING_ACTION]:
    addStopSnoozeForParticipantSoftPendingAction,
  [ActionTypes.REMOVE_SOFT_PENDING_ACTION]: removeSoftPendingAction,
  [ActionTypes.ADD_MARK_PERSONAL_ALARM_DONE_SOFT_PENDING_ACTION]:
    addMarkPersonalAlarmDoneSoftPendingAction,
  [ActionTypes.ADD_SKIP_PERSONAL_ALARM_SOFT_PENDING_ACTION]:
    addSkipPersonalAlarmSoftPendingAction,
  [ActionTypes.ADD_SKIP_PERSONAL_PARTICIPANT_ALARM_SOFT_PENDING_ACTION]:
    addSkipPersonalParticipantAlarmSoftPendingAction,
  [ActionTypes.ADD_SKIP_RECIPIENT_ALARM_SOFT_PENDING_ACTION]:
    addSkipRecipientAlarmSoftPendingAction,
  [ActionTypes.ADD_SKIP_RECIPIENT_CREATOR_ALARM_SOFT_PENDING_ACTION]:
    addSkipRecipientCreatorAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_RECIPIENT_ALARM_DATE_TIME_SOFT_PENDING_ACTION]:
    addUpdateRecipientAlarmDateAndTimeSoftPendingAction,
  [ActionTypes.ADD_MARK_RECIPIENT_ALARM_DONE_SOFT_PENDING_ACTION]:
    addMarkRecipientAlarmDoneSoftPendingAction,
  [ActionTypes.ADD_SET_PARTICIPANT_ALARM_RESPONSE_SOFT_PENDING_ACTION]:
    addSetParticipantAlarmResponseSoftPendingAction,
  [ActionTypes.ADD_SET_RECIPIENT_ALARM_RESPONSE_SOFT_PENDING_ACTION]:
    addSetRecipientAlarmResponseSoftPendingAction,
  [ActionTypes.ADD_SET_PAST_ALARM_OCCURRENCE_RESPONSE_SOFT_PENDING_ACTION]:
    addSetPastAlarmOccurrenceResponseSoftPendingAction,
  [ActionTypes.ADD_UPDATE_PARTICIPANT_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION]:
    addUpdateParticipantAlarmRingerSettingsSoftPendingAction,
  [ActionTypes.ADD_UPDATE_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION]:
    addUpdateAlarmRingerSettingsSoftPendingAction,
  [ActionTypes.ADD_UPDATE_ALARM_PRE_REMINDER_SOFT_PENDING_ACTION]:
    addUpdateAlarmPreReminderDurationSoftPendingAction,
  [ActionTypes.ADD_UPDATE_GLOBAL_ALARM_RINGER_SETTINGS_SOFT_PENDING_ACTION]:
    addUpdateGlobalAlarmRingerSettingsSoftPendingAction,
  [ActionTypes.ADD_UPDATE_RING_PARTICIPANT_ALARMS_BY_DEFAULT_SETTING_SOFT_PENDING_ACTION]:
    addUpdateRingParticipantAlarmsByDefaultSettingSoftPendingAction,
  [ActionTypes.ADD_ADD_ALARM_TO_ALARM_CATEGORY_SOFT_PENDING_ACTION]:
    addAddAlarmToAlarmCategorySoftPendingAction,
  [ActionTypes.ADD_REMOVE_ALARM_FROM_ALARM_CATEGORY_SOFT_PENDING_ACTION]:
    addRemoveAlarmFromAlarmCategorySoftPendingAction,
  [ActionTypes.ADD_DELETE_ALARM_CATEGORY_SOFT_PENDING_ACTION]:
    addDeleteAlarmCategorySoftPendingAction,
  [ActionTypes.ADD_UPDATE_ALARM_CATEGORY_SOFT_PENDING_ACTION]:
    addUpdateAlarmCategorySoftPendingAction,
  [ActionTypes.ADD_CREATE_ALARM_CATEGORY_SOFT_PENDING_ACTION]:
    addCreateAlarmCategorySoftPendingAction,
  [ActionTypes.ADD_MARK_PARTICIPANT_RESPONSE_FOR_GROUP_ALARM_SOFT_PENDING_ACTION]:
    addMarkParticipantResponseForGroupAlarmSoftPendingAction,
  [ActionTypes.ADD_MARK_CREATOR_RESPONSE_FOR_GROUP_ALARM_SOFT_PENDING_ACTION]:
    addMarkCreatorResponseForGroupAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_RING_ON_EARPHONE_ONLY_SOFT_PENDING_ACTION]:
    addUpdateRingOnEarphoneOnlySoftPendingAction,
  [ActionTypes.ADD_UPDATE_GESTURE_TO_DISMISS_ALARM_SOFT_PENDING_ACTION]:
    addUpdateGestureToDismissAnAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_PRE_REMINDER_RINGTONE_SOFT_PENDING_ACTION]:
    addUpdatePreReminderRingtoneSoftPendingAction,
  [ActionTypes.ADD_UPDATE_TIMER_RINGTONE_SOFT_PENDING_ACTION]:
    addUpdateTimerRingtoneSoftPendingAction,
  [ActionTypes.ADD_UPDATE_MUTE_ALARMS_SOFT_PENDING_ACTION]:
    addUpdateMuteAlarmsSoftPendingAction,
  [ActionTypes.ADD_UPDATE_SPECIFY_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION]:
    addUpdateSpecifyTimezoneForAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_DEFAULT_TIMEZONE_FOR_ALARM_SOFT_PENDING_ACTION]:
    addUpdateDefaultTimezoneForAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_GRADUALLY_INCREASE_VOLUME_SOFT_PENDING_ACTION]:
    addUpdateGraduallyIncreaseVolumeSoftPendingAction,
  [ActionTypes.ADD_UPDATE_SHAKE_TO_STOP_ALARM_SOFT_PENDING_ACTION]:
    addUpdateShakeToStopAlarmSoftPendingAction,
  [ActionTypes.ADD_UPDATE_SHAKE_SENSITIVITY_SOFT_PENDING_ACTION]:
    addUpdateShakeSensitivitySoftPendingAction,
  [ActionTypes.ADD_UPDATE_PIN_MODE_SOFT_PENDING_ACTION]:
    addUpdatePinModeSoftPendingAction,
  [ActionTypes.ADD_UPDATE_PARTICIPANT_ALARM_PRE_REMINDER_DURATION_SOFT_PENDING_ACTION]:
    addUpdateParticipantAlarmPreReminderDurationSoftPendingAction,
  [ActionTypes.ADD_UPDATE_NOTIFICATION_SETTINGS_SOFT_PENDING_ACTION]:
    addUpdateNotificationSettingsSoftPendingAction,
  [ActionTypes.ADD_ADD_CHECKLIST_PENDING_ACTION]: addAddChecklistPendingAction,
  [ActionTypes.ADD_EDIT_CHECKLIST_PENDING_ACTION]:
    addEditChecklistPendingAction,
  [ActionTypes.ADD_REMOVE_CHECKLIST_PENDING_ACTION]:
    addRemoveChecklistPendingAction,
  [ActionTypes.ADD_UPDATE_NOTIFICATION_ACTION_BEHAVIOR_SOFT_PENDING_ACTION]:
    addUpdateNotificationActionBehaviorSoftPendingAction,
  [ActionTypes.ADD_UPDATE_PROMPT_FOR_UPDATE_SOFT_PENDING_ACTION]:
    addUpdatePromptForUpdateSoftPendingAction,
  [ActionTypes.ADD_REMOVE_REMOTE_ALARM_NOTIFICATION_PENDING_ACTION]:
    addRemoveRemoteAlarmNotificationPendingAction,
  [ActionTypes.ADD_UPDATE_SORT_ALARM_CATEGORIES_BY_SOFT_PENDING_ACTION]:
    addUpdateSortAlarmCategoriesBySoftPendingAction,
  [ActionTypes.ADD_UPDATE_MISSED_ALARM_PLACEMENT_SETTING_SOFT_PENDING_ACTION]:
    addUpdateMissedAlarmPlacementSettingSoftPendingAction,
  [ActionTypes.ADD_UPDATE_FAB_BUTTON_BEHAVIOR_SOFT_PENDING_ACTION]:
    addUpdateFabButtonBehaviorSoftPendingAction
}

export default createReducer(INITIAL_STATE, ACTION_HANDLERS)
